import { API_URL } from './DbConfig';

/**
 * SessionManager handles session expiration gracefully by:
 * 1. Validating the session when the tab becomes visible
 * 2. Prompting the user to log in again on 401/403 responses
 * 3. Ignoring stale auth failures from an older session generation
 */

let hasHandledAuthExpiry = false;
let hasPendingAuthExpiry = false;
let isAuthPromptVisible = false;
let authStateVersion = 0;
export const AUTH_EXPIRED_EVENT = 'app:auth-expired';

export class AuthExpiredError extends Error {
    constructor(message = 'Authentication expired') {
        super(message);
        this.name = 'AuthExpiredError';
    }
}

export const isAuthExpiredError = (error: unknown): error is AuthExpiredError => {
    return error instanceof AuthExpiredError
        || (error instanceof Error && error.name === 'AuthExpiredError');
};

/**
 * Detects which app is running based on URL path or port
 */
export const isAdminApp = (): boolean => {
    const { hostname, port, pathname } = window.location;
    
    // Local dev: admin runs on port 3030, monitor on 3060
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return port === '3030';
    }
    
    // Prod/Beta: check URL path or subdomain
    return pathname.startsWith('/admin');
};

/**
 * Pings the API session endpoint to validate the existing cookie-based session
 * without exposing the browser directly to CouchDB's Basic auth challenge
 */
const pingSession = async (): Promise<void> => {
    const requestAuthVersion = authStateVersion;

    try {
        const res = await fetch(`${API_URL}/account/session`, {
            method: 'GET',
            credentials: 'include',
        });

        if (res.ok) {
            markSessionAuthenticated();
            return;
        }

        if (isAuthFailureStatus(res.status)) {
            expireSession(requestAuthVersion);
        }
    } catch (error) {
        console.warn('Session ping failed:', error);
    }
};

const clearClientSessionState = (): void => {
    localStorage.removeItem('usernameAdmin');
    localStorage.removeItem('usernameMonitor');
    localStorage.removeItem('passwordAdmin');
    localStorage.removeItem('passwordMonitor');
    localStorage.removeItem('app.currentUser');
    localStorage.removeItem('app.currentTenant');
};

const isAuthFailureStatus = (status: number): boolean => {
    return status === 401 || status === 403;
};

const bumpAuthStateVersion = (): void => {
    authStateVersion += 1;
};

export const markSessionAuthenticated = (): void => {
    hasHandledAuthExpiry = false;
    hasPendingAuthExpiry = false;
    isAuthPromptVisible = false;
    bumpAuthStateVersion();
};

const finalizeAuthExpiry = (): void => {
    if (hasHandledAuthExpiry) {
        return;
    }

    hasHandledAuthExpiry = true;
    hasPendingAuthExpiry = false;
    clearClientSessionState();
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));

    if (window.location.hash !== '#/login') {
        window.location.hash = '/login';
    }
};

const promptForReauthentication = (): void => {
    if (isAuthPromptVisible) {
        return;
    }

    isAuthPromptVisible = true;

    const shouldRedirectToLogin = window.confirm(
        'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'
    );

    isAuthPromptVisible = false;

    if (shouldRedirectToLogin) {
        finalizeAuthExpiry();
        return;
    }

    hasPendingAuthExpiry = false;
};

const expireSession = (requestAuthVersion: number): AuthExpiredError => {
    if (requestAuthVersion !== authStateVersion || hasHandledAuthExpiry || hasPendingAuthExpiry) {
        return new AuthExpiredError();
    }

    hasPendingAuthExpiry = true;
    bumpAuthStateVersion();
    promptForReauthentication();

    return new AuthExpiredError();
};

export const logoutCurrentSession = async (): Promise<boolean> => {
    try {
        const res = await fetch(`${API_URL}/account/logout`, {
            method: 'POST',
            credentials: 'include',
        });

        if (!res.ok && !isAuthFailureStatus(res.status)) {
            return false;
        }

        clearClientSessionState();
        hasHandledAuthExpiry = false;
        hasPendingAuthExpiry = false;
        isAuthPromptVisible = false;
        bumpAuthStateVersion();
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
        return true;
    } catch (error) {
        console.error('Logout failed:', error);
        return false;
    }
};

/**
 * Wraps the native fetch to detect authentication failures (401/403)
 * and prompt the user to reload the page
 */
export const authenticatedFetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
): Promise<Response> => {
    const requestAuthVersion = authStateVersion;
    const response = await fetch(input, init);

    if (isAuthFailureStatus(response.status)) {
        throw expireSession(requestAuthVersion);
    }

    return response;
};

/**
 * Initialize session management:
 * - Set up visibility change listener to ping session when tab becomes active
 */
export const initializeSessionManager = (): void => {
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            pingSession();
        }
    });

    window.addEventListener('beforeunload', () => {
        hasHandledAuthExpiry = false;
        hasPendingAuthExpiry = false;
        isAuthPromptVisible = false;
    });
};
